import isBreakpoint from '@/utils/dom/isBreakpoint'

import { DOWNLOAD_CENTRE_POPOVER_ID } from './DownloadCentre.constants'

export const getDownloadCentrePopover = (): HTMLElement | null =>
	document.getElementById(DOWNLOAD_CENTRE_POPOVER_ID)

export const isDownloadCentrePopoverOpen = (
	popover: HTMLElement | null = getDownloadCentrePopover()
): boolean => {
	if (!popover) {
		return false
	}

	try {
		return popover.matches(':popover-open')
	} catch {
		return false
	}
}

const isOpenPopover = (element: Element): boolean => {
	try {
		return element.matches(':popover-open')
	} catch {
		return false
	}
}

const getOpenDialogs = (): HTMLDialogElement[] =>
	Array.from(document.querySelectorAll('dialog[open]'))

const getOpenPopoversExcludingDownloadCentre = (): HTMLElement[] =>
	Array.from(document.querySelectorAll('[popover]')).filter(
		(element): element is HTMLElement =>
			element instanceof HTMLElement &&
			element.id !== DOWNLOAD_CENTRE_POPOVER_ID &&
			isOpenPopover(element)
	)

export const hasOtherOpenOverlay = (): boolean =>
	getOpenDialogs().length > 0 ||
	getOpenPopoversExcludingDownloadCentre().length > 0

export const closeOtherOpenOverlays = (): void => {
	getOpenDialogs().forEach((dialog) => {
		try {
			dialog.close()
		} catch {
			// jsdom and some engines do not support dialog.close
		}
	})

	getOpenPopoversExcludingDownloadCentre().forEach((popover) => {
		if (typeof popover.hidePopover !== 'function') {
			return
		}

		try {
			popover.hidePopover()
		} catch {
			// jsdom and some engines do not support the Popover API
		}
	})
}

const callDownloadCentrePopover = (
	method: 'hidePopover' | 'showPopover'
): void => {
	const popover = getDownloadCentrePopover()

	if (!popover || typeof popover[method] !== 'function') {
		return
	}

	try {
		popover[method]()
	} catch {
		// jsdom and some engines do not support the Popover API
	}
}

export const showDownloadCentrePopover = (): void => {
	callDownloadCentrePopover('showPopover')
}

const clearDownloadCentreVisibilityOverrides = (popover: HTMLElement): void => {
	popover.style.removeProperty('display')
	popover.style.removeProperty('opacity')
	popover.style.removeProperty('transform')
}

export const restoreDownloadCentrePopoverForInteraction = (): void => {
	const popover = getDownloadCentrePopover()

	if (!popover) {
		return
	}

	clearDownloadCentreVisibilityOverrides(popover)

	if (!isDownloadCentrePopoverOpen(popover)) {
		showDownloadCentrePopover()
	}
}

export const hideDownloadCentrePopover = (): void => {
	const popover = getDownloadCentrePopover()

	if (popover) {
		// Clear temporary inline styles from keepDownloadCentreVisible so the popover can close normally.
		clearDownloadCentreVisibilityOverrides(popover)
	}

	callDownloadCentrePopover('hidePopover')
}

// Light-dismiss would close the download centre when the editor moves to another track's WAV control.
// Keep it open so they can see the new row appear.
// DEFER: Cover keepDownloadCentreVisible popover override behaviour in Cypress once download centre e2e exists.
export const keepDownloadCentreVisible = (): void => {
	if (isBreakpoint('mobile')) {
		return
	}

	const popover = getDownloadCentrePopover()

	if (!popover || !isDownloadCentrePopoverOpen(popover)) {
		return
	}

	popover.style.setProperty('display', 'block', 'important')
	popover.style.setProperty('opacity', '1', 'important')
	popover.style.setProperty('transform', 'none', 'important')
}
